/**
 * What people talk to Electra about, and how: the browser, the search, and one transcript.
 *
 * Opening a transcript is written to that customer's audit trail by the API. That is the deal
 * that makes reading other people's conversations acceptable: it is never silent.
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { request } from '../api/client';
import type {
  ConversationList,
  ConversationRow,
  MessageSearch,
  Transcript,
  TranscriptTurn,
} from '../api/types';
import { clock, duration, moment, num, since } from '../components/format';
import { Failed, Loading, Metric, Mode, PageHead, Panel, Pill } from '../components/ui';

export function useConversations({
  organisationId,
  userId,
  search,
}: {
  organisationId?: string | null;
  userId?: string | null;
  search?: string;
}) {
  const params = new URLSearchParams({ limit: '150' });
  if (organisationId) params.set('organisation_id', organisationId);
  if (userId) params.set('user_id', userId);
  if (search?.trim()) params.set('search', search.trim());
  return useQuery({
    queryKey: ['conversations', params.toString()],
    queryFn: () => request<ConversationList>(`/api/v1/admin/conversations?${params.toString()}`),
    placeholderData: keepPreviousData,
  });
}

export function ConversationTable({
  rows,
  hideOrganisation,
}: {
  rows: ConversationRow[];
  hideOrganisation?: boolean;
}) {
  if (rows.length === 0)
    return <div className="panel-body muted">No conversation matches that.</div>;
  return (
    <table>
      <thead>
        <tr>
          <th>Conversation</th>
          {hideOrganisation ? null : <th>Customer</th>}
          <th>Person</th>
          <th>How</th>
          <th className="numeric">Messages</th>
          <th className="numeric">Failed</th>
          <th>Last said</th>
          <th>Updated</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.conversation_id}>
            <td>
              <Link to={`/conversations/${row.conversation_id}`}>{row.title || 'Untitled'}</Link>
            </td>
            {hideOrganisation ? null : (
              <td>
                <Link to={`/customers/${row.organisation_id}`} className="muted">
                  {row.organisation_name}
                </Link>
              </td>
            )}
            <td className="muted">{row.user_email}</td>
            <td>
              <span className="row" style={{ gap: 8 }}>
                {row.chat_turns ? <span className="mode">{num(row.chat_turns)} text</span> : null}
                {row.voice_turns ? (
                  <span className="mode voice">{num(row.voice_turns)} voice</span>
                ) : null}
                {row.night_shift_runs ? (
                  <span className="mode night_shift">{num(row.night_shift_runs)} night</span>
                ) : null}
              </span>
            </td>
            <td className="numeric">{num(row.messages)}</td>
            <td className={`numeric ${row.failed_turns ? 'bad' : 'muted'}`}>
              {row.failed_turns || '—'}
            </td>
            <td className="muted wrap small">
              {row.last_message_role ? (
                <span className="faint">{row.last_message_role}: </span>
              ) : null}
              {row.last_message ?? '—'}
            </td>
            <td className="muted">{since(row.updated_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Conversations() {
  const [params, setParams] = useSearchParams();
  const organisationId = params.get('organisation_id');
  const userId = params.get('user_id');
  const [search, setSearch] = useState('');
  const [words, setWords] = useState('');
  const [submitted, setSubmitted] = useState('');

  const list = useConversations({ organisationId, userId, search });
  const hits = useQuery({
    queryKey: ['messages', submitted, organisationId],
    queryFn: () => {
      const q = new URLSearchParams({ q: submitted, limit: '80' });
      if (organisationId) q.set('organisation_id', organisationId);
      return request<MessageSearch>(`/api/v1/admin/messages/search?${q.toString()}`);
    },
    enabled: submitted.trim().length >= 2,
  });

  return (
    <>
      <PageHead
        eyebrow="Conversations"
        title="What they talk to Electra about"
        note="Newest first, across every customer. Opening one is written to that customer's audit trail."
        actions={
          organisationId || userId ? (
            <button type="button" className="button-quiet" onClick={() => setParams({})}>
              Clear filter
            </button>
          ) : null
        }
      />

      <Panel eyebrow="Search" title="Find the words">
        <form
          className="toolbar"
          style={{ marginBottom: 0 }}
          onSubmit={(event) => {
            event.preventDefault();
            setSubmitted(words);
          }}
        >
          <input
            type="search"
            placeholder="A word or phrase anybody said or was told…"
            value={words}
            onChange={(event) => setWords(event.target.value)}
            style={{ minWidth: 340, flex: 1 }}
            aria-label="Search messages"
          />
          <button type="submit" className="button" disabled={words.trim().length < 2}>
            Search
          </button>
        </form>
        {hits.error ? <Failed error={hits.error} fallback="Search failed." /> : null}
        {hits.isFetching ? <Loading what="Searching" /> : null}
        {hits.data ? (
          hits.data.hits.length === 0 ? (
            <p className="muted" style={{ margin: '12px 0 0' }}>
              Nobody has said “{hits.data.query}”.
            </p>
          ) : (
            <div className="table-wrap" style={{ marginTop: 12 }}>
              <table>
                <thead>
                  <tr>
                    <th>Said</th>
                    <th>Who</th>
                    <th>How</th>
                    <th>Conversation</th>
                    <th>When</th>
                  </tr>
                </thead>
                <tbody>
                  {hits.data.hits.map((hit) => (
                    <tr key={hit.message_id}>
                      <td className="wrap">
                        <span className="faint small">{hit.role}: </span>
                        {hit.snippet}
                      </td>
                      <td className="muted">
                        {hit.user_email ?? '—'}
                        <div className="small faint">{hit.organisation_name}</div>
                      </td>
                      <td>
                        <Mode value={hit.input_mode} />
                      </td>
                      <td>
                        <Link to={`/conversations/${hit.conversation_id}`}>
                          {hit.conversation_title || 'Untitled'}
                        </Link>
                      </td>
                      <td className="muted">{moment(hit.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )
        ) : null}
      </Panel>

      <div className="toolbar">
        <input
          type="search"
          placeholder="Filter by title, person or customer…"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          style={{ minWidth: 300 }}
          aria-label="Filter conversations"
        />
        {organisationId ? <span className="pill">one customer</span> : null}
        {userId ? <span className="pill">one person</span> : null}
      </div>
      {list.error ? <Failed error={list.error} fallback="Could not load conversations." /> : null}
      <Panel flush>
        {list.isPending || !list.data ? (
          <div className="panel-body muted">Loading…</div>
        ) : (
          <ConversationTable rows={list.data.conversations} />
        )}
      </Panel>
    </>
  );
}

export function ConversationView() {
  const { conversationId = '' } = useParams();
  const { data, isPending, error } = useQuery({
    queryKey: ['transcript', conversationId],
    queryFn: () => request<Transcript>(`/api/v1/admin/conversations/${conversationId}`),
    // A transcript is read, not watched. Refetching it on focus would write another audit line
    // every time the operator tabbed back, which would make the trail say something untrue.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
  if (isPending) return <Loading what="Opening the transcript" />;
  if (error) return <Failed error={error} fallback="Could not open this conversation." />;

  const { conversation, turns } = data;
  return (
    <>
      <PageHead
        eyebrow="Transcript"
        title={conversation.title || 'Untitled conversation'}
        note={
          <>
            <Link to={`/customers/${conversation.organisation_id}`}>
              {conversation.organisation_name}
            </Link>{' '}
            · {conversation.user_email} · started {moment(conversation.created_at)}
          </>
        }
        actions={
          <Link
            className="button-quiet"
            to={`/conversations?organisation_id=${conversation.organisation_id}`}
          >
            Their other conversations
          </Link>
        }
      />
      <div className="cards">
        <Metric label="Messages" value={num(conversation.messages)} small />
        <Metric label="Text turns" value={num(conversation.chat_turns)} small />
        <Metric label="Voice turns" value={num(conversation.voice_turns)} small />
        <Metric
          label="Failed"
          value={num(conversation.failed_turns)}
          small
          {...(conversation.failed_turns ? { tone: 'bad' as const } : {})}
        />
      </div>
      <Panel>
        <div className="transcript" style={{ padding: 0 }}>
          {turns.map((turn) => (
            <Turn key={turn.message_id} turn={turn} />
          ))}
        </div>
      </Panel>
    </>
  );
}

function Turn({ turn }: { turn: TranscriptTurn }) {
  const failed = turn.run_status === 'failed';
  return (
    <div className={`turn ${turn.role}${failed ? ' failed' : ''}`}>
      <div className="bubble">{turn.content || <span className="faint">(empty)</span>}</div>
      <div className="turn-meta">
        <span>{clock(turn.created_at)}</span>
        <Mode value={turn.input_mode} />
        {turn.role === 'assistant' && turn.model ? (
          <span>
            {turn.provider}/{turn.model}
          </span>
        ) : null}
        {turn.duration_ms !== null ? <span>{duration(turn.duration_ms)}</span> : null}
        {turn.run_status && turn.run_status !== 'completed' ? (
          <Pill value={turn.run_status} />
        ) : null}
      </div>
      {turn.error_summary ? <div className="bad small">{turn.error_summary}</div> : null}
      {turn.tool_calls.map((call, index) => (
        <details key={`${call.tool_name}:${index}`} className="tool-call">
          <summary>
            <span className="good">{call.tool_name}</span>
            <Pill value={call.status} />
            {call.approval_status ? (
              <span className="muted small">approval {call.approval_status}</span>
            ) : null}
            {call.result_summary ? (
              <span className="muted small">— {call.result_summary}</span>
            ) : null}
          </summary>
          <pre>{JSON.stringify(call.arguments, null, 2)}</pre>
        </details>
      ))}
    </div>
  );
}
